import ReactDOM from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import AdminResourceTable from '@/admin/AdminResourceTable'
import AdminUserTable from '@/admin/AdminUserTable'

import AdminResourceForm from '@/forms/admin/AdminResourceForm'
import AdminUserForm from '@/forms/admin/AdminUserForm'

import ParentDashboard from '@/app/ParentDashboard'
import ResourceLibrary from '@/app/ResourceLibrary'
import SingleResource from '@/app/SingleResource'

import 'regenerator-runtime'
import './index.css'

import AdminLayout from '@/admin/_Layout'
import LoginPage from '@/auth/LoginPage'
import LogoutPage from '@/auth/LogoutPage'
import RegisterPage from '@/auth/RegisterPage'
import RequestResetPage from '@/auth/RequestPasswordResetPage'
import ResetPasswordPage from '@/auth/ResetPasswordPage'
import ForbiddenPage from '@/error-pages/ForbiddenPage'
import NotFoundPage from '@/error-pages/NotFoundPage'

import {
    AuthenticatedWrapper,
    AuthProvider,
    OnboardingWrapper,
    PermissionsWrapper,
} from '@/services/authentication.service'
import { ResourceStatusProvider } from '@/services/resource.service'

import AppLayout from '@/app/_Layout'
import AIChat from '@/app/AIChat'
import Onboarding from '@/app/Onboarding'
import RoadmapJourney from '@/app/RoadmapJourney'

import AdminAnalytics from './admin/AdminAnalytics'
import AdminSystem from './admin/AdminSystem'
import AdminAppSettings from './admin/AdminAppSettings'
import AdminChatPromptForm from './admin/AdminChatPromptSettings'
import AdminKnowledgeBaseTable from './admin/AdminKnowledgeTable'
import AdminProviderCatTable from './admin/AdminProviderCatTable'
import AdminProvidersTable from './admin/AdminProvidersTable'
import AdminRoadmapMilestonesTable from './admin/AdminRoadmapMilestonesTable'
import AdminRoadmapSequencesTable from './admin/AdminRoadmapSequencesTable'
import AdminOrganizationsTable from './admin/AdminOrganizationsTable'
import AdminOrganizationForm from './forms/admin/AdminOrganizationForm'
import ProviderApp from './provider/ProviderApp'
import FamilyCareTeamPreviewLayout from './admin/family-care-team-preview/_Layout'
import CareTeamHome from './admin/family-care-team-preview/CareTeamHome'
import AddProvider from './admin/family-care-team-preview/AddProvider'
import ProviderDetail from './admin/family-care-team-preview/ProviderDetail'
import UploadAndAnalyze from './admin/family-care-team-preview/UploadAndAnalyze'
import Roadmap from './admin/family-care-team-preview/Roadmap'
import Conflicts from './admin/family-care-team-preview/Conflicts'
import ConflictDetail from './admin/family-care-team-preview/ConflictDetail'
import FamilyAppPreview from './admin/family-care-team-preview/FamilyAppPreview'
import ProviderPreviewLayout from './admin/provider-preview/_Layout'
import PreviewProviderLogin from './admin/provider-preview/ProviderLogin'
import PreviewProviderDashboard from './admin/provider-preview/ProviderDashboard'
import PreviewProviderTeam from './admin/provider-preview/ProviderTeam'
import PreviewProviderTeamMemberDetail from './admin/provider-preview/ProviderTeamMemberDetail'
import PreviewProviderLicenses from './admin/provider-preview/ProviderLicenses'
import PreviewProviderFamilies from './admin/provider-preview/ProviderFamilies'
import PreviewProviderFamilyDetail from './admin/provider-preview/ProviderFamilyDetail'
import PreviewProviderFormerPatient from './admin/provider-preview/ProviderFormerPatient'
import PreviewProviderSettings from './admin/provider-preview/ProviderSettings'
import CommunityPage from './app/CommunityPage'
import PrivacyPolicy from './app/PrivacyPolicy'
import ProviderSearchPage from './app/Providers'
import CategoryTiles from './app/RescourcesLanding'
import SavedResourcesPage from './app/SavedResources'
import SchedulesCalendarPage from './app/Schedules'
import SubscriptionPage from './app/Subscription'
import TermsOfService from './app/TermsOfService'
import UserProfileForm from './app/UserProfile'
import HelpPage from './app/HelpPage'
import ViewPostPage from './app/ViewPost'
import AnalyticsProvider from './components/AnalyticsProvider'
import AndroidInstallPrompt from './components/AndroidInstallPrompt'
import IOSInstallPrompt from './components/IOSInstallPrompt'
import AdminKnowledgeForm from './forms/admin/AdminKnowledgeForm'
import AdminProviderCatForm from './forms/admin/AdminProviderCatForm'
import AdminProviderForm from './forms/admin/AdminProviderForm'
import AdminRoadmapMilestoneForm from './forms/admin/AdminRoadmapMilestoneForm'
import AdminRoadmapSequenceForm from './forms/admin/AdminRoadmapSequenceForm'
import UserEditProfileForm from './forms/UserEditProfileForm'
import { ChatProvider } from './services/chat.service'
import ManageSubscription from './stripe/ManageSubscription'
import { PaywallProvider } from './stripe/PayWallProvider'

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

// The coordinator portal is the same build served from the provider. host
// (provider.brightpathforautism[-dev].com; provider.localhost:5173 for local
// dev). On that host only the provider route tree exists — family and admin
// routes are not registered, and vice versa.
const isProviderHost =
    window.location.hostname === 'provider.localhost' || window.location.hostname.startsWith('provider.')

if (isProviderHost) {
    root.render(
        <BrowserRouter>
            <ProviderApp />
        </BrowserRouter>
    )
} else {
root.render(
    <BrowserRouter>
        <AnalyticsProvider>
            <ResourceStatusProvider>
                <AuthProvider>
                    <ChatProvider>
                        <Routes>
                            {/* Authentication Pages */}
                            <Route path='/register' element={<RegisterPage />} />
                            <Route path='/login' element={<LoginPage />} />
                            <Route path='/logout' element={<LogoutPage />} />
                            <Route path='/request-reset' element={<RequestResetPage />} />
                            <Route path='/reset-password' element={<ResetPasswordPage />} />

                            {/* Public Routes */}
                            <Route path='/onboarding' element={<Onboarding />} />
                            <Route path='/terms-of-service' element={<TermsOfService />} />
                            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                            <Route element={<AuthenticatedWrapper />}>
                                <Route path='stripe-callback' element={<SubscriptionPage />} />
                                <Route element={<OnboardingWrapper />}>
                                    <Route
                                        path='/'
                                        element={
                                            <PaywallProvider>
                                                <AppLayout />
                                            </PaywallProvider>
                                        }
                                    >
                                        <Route path='/' element={<ParentDashboard />} />
                                        <Route path='roadmap' element={<RoadmapJourney />} />
                                        {/* <Route path='roadmap/:id' element={<RoadmapPage />} /> */}
                                        <Route path='/saved-resources' element={<SavedResourcesPage />} />
                                        <Route path='resource-landing' element={<CategoryTiles />} />
                                        <Route path='resource-library' element={<ResourceLibrary />} />
                                        <Route path='chat' element={<AIChat />} />
                                        <Route path='resource/:id' element={<SingleResource />} />
                                        <Route path='profile' element={<UserProfileForm />} />
                                        <Route path='schedule' element={<SchedulesCalendarPage />} />
                                        <Route path='community' element={<CommunityPage />} />
                                        <Route path='/view-post/:id' element={<ViewPostPage />} />
                                        {/* <Route path='/providers' element={<ProviderSearchPage />} /> */}
                                        <Route path='/manage-profile' element={<UserEditProfileForm />} />

                                        <Route path='/manage-subscription' element={<ManageSubscription />} />
                                        <Route path='/help' element={<HelpPage />} />
                                    </Route>
                                </Route>
                                <Route element={<PermissionsWrapper required={{ isAdmin: true }} />}>
                                    <Route path='/admin/family-care-team-preview' element={<FamilyCareTeamPreviewLayout />}>
                                        <Route index element={<Navigate to='team' replace />} />
                                        <Route path='team' element={<CareTeamHome />} />
                                        <Route path='team/add' element={<AddProvider />} />
                                        <Route path='team/:id' element={<ProviderDetail />} />
                                        <Route path='upload' element={<UploadAndAnalyze />} />
                                        <Route path='roadmap' element={<Roadmap />} />
                                        <Route path='conflicts' element={<Conflicts />} />
                                        <Route path='conflicts/:id' element={<ConflictDetail />} />
                                        <Route path='family-view' element={<FamilyAppPreview />} />
                                    </Route>
                                    <Route path='/admin/provider-preview' element={<ProviderPreviewLayout />}>
                                        <Route index element={<Navigate to='dashboard' replace />} />
                                        <Route path='login' element={<PreviewProviderLogin />} />
                                        <Route path='dashboard' element={<PreviewProviderDashboard />} />
                                        <Route path='team' element={<PreviewProviderTeam />} />
                                        <Route path='team/former/:id' element={<PreviewProviderTeamMemberDetail former />} />
                                        <Route path='team/:id' element={<PreviewProviderTeamMemberDetail />} />
                                        <Route path='licenses' element={<PreviewProviderLicenses />} />
                                        <Route path='clients' element={<PreviewProviderFamilies />} />
                                        <Route path='clients/former/:id' element={<PreviewProviderFormerPatient />} />
                                        <Route path='clients/:id' element={<PreviewProviderFamilyDetail />} />
                                        <Route path='settings' element={<PreviewProviderSettings />} />
                                    </Route>
                                    <Route path='/admin' element={<AdminLayout />}>
                                        <Route index element={<AdminAnalytics />} />
                                        <Route path='analytics' element={<AdminAnalytics />} />
                                        <Route path='system' element={<AdminSystem />} />
                                        <Route path='users' element={<AdminUserTable />} />
                                        <Route path='user/:id' element={<AdminUserForm />} />
                                        <Route path='resources' element={<AdminResourceTable />} />
                                        <Route path='resource/:id' element={<AdminResourceForm />} />
                                        <Route path='knowledge-base' element={<AdminKnowledgeBaseTable />} />
                                        <Route path='knowledge/:id' element={<AdminKnowledgeForm />} />
                                        <Route path='chat-prompt-settings' element={<AdminChatPromptForm />} />
                                        <Route path='providers' element={<AdminProvidersTable />} />
                                        <Route path='knowledge/:id' element={<AdminKnowledgeForm />} />
                                        <Route path='provider/:id' element={<AdminProviderForm />} />
                                        <Route path='provider-categories' element={<AdminProviderCatTable />} />
                                        <Route path='app-settings' element={<AdminAppSettings />} />
                                        <Route path='provider-category/:id' element={<AdminProviderCatForm />} />
                                        <Route path='roadmap-milestones' element={<AdminRoadmapMilestonesTable />} />
                                        <Route path='roadmap-milestone/:id' element={<AdminRoadmapMilestoneForm />} />
                                        <Route path='roadmap-sequences' element={<AdminRoadmapSequencesTable />} />
                                        <Route path='roadmap-sequence/:id' element={<AdminRoadmapSequenceForm />} />
                                        <Route path='organizations' element={<AdminOrganizationsTable />} />
                                        <Route path='organization/:id' element={<AdminOrganizationForm />} />
                                    </Route>
                                </Route>
                            </Route>

                            {/* Error */}
                            <Route path='/forbidden' element={<ForbiddenPage />} />
                            <Route path='/*' element={<NotFoundPage />} />
                        </Routes>
                    </ChatProvider>
                    <ToastContainer position='top-center' autoClose={1000} />
                    <IOSInstallPrompt />
                    <AndroidInstallPrompt />
                </AuthProvider>
            </ResourceStatusProvider>
        </AnalyticsProvider>
    </BrowserRouter>
)
}
